import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle, Warning, XCircle, Sparkle, Info } from "@phosphor-icons/react";

const COLOR_MAP = {
  green:  { text: "text-teal",   bg: "bg-teal-soft",   ring: "stroke-teal",   dot: "bg-teal" },
  yellow: { text: "text-amber-600", bg: "bg-amber-100 dark:bg-amber-500/10", ring: "stroke-amber-500", dot: "bg-amber-500" },
  red:    { text: "text-red-600",   bg: "bg-red-100 dark:bg-red-500/10",     ring: "stroke-red-500",   dot: "bg-red-500" },
  gray:   { text: "text-muted-foreground", bg: "bg-secondary", ring: "stroke-muted-foreground", dot: "bg-muted-foreground" },
};

function CompositeRing({ score, color = "green", size = 140 }) {
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score || 0));
  const offset = c - (pct / 100) * c;
  const palette = COLOR_MAP[color] || COLOR_MAP.gray;
  return (
    <svg width={size} height={size} className="-rotate-90" role="img" aria-label={`OIS ${pct}/100`}>
      <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none" className="stroke-border" />
      <circle
        cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none"
        className={`transition-all duration-700 ${palette.ring}`}
        strokeDasharray={c}
        strokeDashoffset={offset}
        strokeLinecap="round"
      />
      <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" className={`rotate-90 origin-center fill-foreground font-display`} style={{ transform: "rotate(90deg)", transformOrigin: "center" }} fontSize={size * 0.28} fontWeight="500">
        {Math.round(pct)}
      </text>
    </svg>
  );
}

function RecoBadge({ code, color }) {
  const palette = COLOR_MAP[color] || COLOR_MAP.gray;
  const Icon = code === "INVEST" ? CheckCircle : code === "REVIEW" ? Warning : XCircle;
  return (
    <div className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.16em] px-3 py-1.5 rounded-full ${palette.bg} ${palette.text}`} data-testid="ois-reco-badge">
      <Icon size={12} weight="fill" /> {code}
    </div>
  );
}

/**
 * OISCard — renders the Orixa Investment Score (OIS™) composite for an assessment.
 * Fetches from GET /api/assessments/{id}/ois. Non-breaking: does not modify
 * the assessment document. Falls back gracefully if endpoint returns no signal.
 */
export default function OISCard({ assessmentId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const { data } = await api.get(`/assessments/${assessmentId}/ois`);
        if (!stop) setData(data);
      } catch (e) {
        if (!stop) setErr(e);
      }
    })();
    return () => { stop = true; };
  }, [assessmentId]);

  if (err) return null;
  if (!data) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6 card-elev animate-pulse" data-testid="ois-loading">
        <div className="h-4 w-24 bg-secondary rounded mb-4" />
        <div className="h-32 bg-secondary rounded" />
      </div>
    );
  }

  const inv = data.investment_grade || {};
  const risk = data.risk_grade || {};
  const reco = data.recommendation || {};
  const conf = data.confidence || {};
  const compositeColor = COLOR_MAP[inv.color] || COLOR_MAP.gray;

  return (
    <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 card-elev" data-testid="ois-card">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="inline-flex items-center gap-1.5 text-overline text-primary mb-1.5">
            <Sparkle size={12} weight="fill" /> Orixa Investment Score
            <span className="text-[9px] uppercase tracking-widest opacity-70">™</span>
          </div>
          <div className="text-xs text-muted-foreground">Weighting profile: <span className="font-semibold text-foreground">{data.profile_label}</span></div>
        </div>
        <RecoBadge code={reco.code} color={reco.color} />
      </div>

      {/* Composite + grades */}
      <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-6 items-center">
        <div className="flex justify-center md:justify-start">
          <CompositeRing score={data.score} color={inv.color} size={148} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="rounded-lg border border-border p-3">
            <div className="text-overline text-muted-foreground">Grade</div>
            <div className={`mt-1 data-large text-2xl ${compositeColor.text}`} data-testid="ois-grade">{inv.grade || "—"}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{inv.band || ""}</div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="text-overline text-muted-foreground">Risk</div>
            <div className={`mt-1 data-large text-2xl ${COLOR_MAP[risk.color]?.text || "text-foreground"}`} data-testid="ois-risk-grade">{risk.grade || "—"}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{risk.band || ""}</div>
          </div>
          <div className="rounded-lg border border-border p-3 col-span-2 sm:col-span-1">
            <div className="text-overline text-muted-foreground">Confidence</div>
            <div className="mt-1 data-large text-2xl" data-testid="ois-confidence">{conf.band || "—"}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{conf.coverage_pct}% coverage</div>
          </div>
        </div>
      </div>

      {/* Recommendation rationale */}
      {reco.rationale && (
        <p className="mt-6 pt-6 border-t border-border text-sm text-muted-foreground leading-relaxed" data-testid="ois-reco-rationale">
          <span className={`font-semibold ${compositeColor.text}`}>{reco.label} — </span>{reco.rationale}
        </p>
      )}

      {/* 12 sub-scores */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="text-overline text-muted-foreground mb-4 flex items-center gap-1.5">
          Twelve-dimension breakdown
          <span className="text-[10px] text-muted-foreground/70 normal-case tracking-normal">(weighted for {data.profile_label})</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="ois-breakdown">
          {(data.breakdown || []).map((b) => {
            const palette = COLOR_MAP[b.color] || COLOR_MAP.gray;
            const pct = b.has_signal ? b.score : 0;
            return (
              <div key={b.dimension} className="flex items-center gap-3" data-testid={`ois-dim-${b.dimension}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-sm font-semibold truncate">{b.label}</div>
                    <div className={`text-sm font-mono ${b.has_signal ? palette.text : "text-muted-foreground/60"}`}>
                      {b.has_signal ? `${Math.round(b.score)}` : "—"}
                    </div>
                  </div>
                  <div className="mt-1.5 h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${b.has_signal ? palette.dot : "bg-muted-foreground/30"}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="mt-1 text-[10px] text-muted-foreground">Weight {b.weight_pct}%{!b.has_signal && " · Insufficient signal"}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Methodology footnote */}
      <p className="mt-6 pt-6 border-t border-border text-[11px] text-muted-foreground leading-relaxed flex items-start gap-2">
        <Info size={12} weight="bold" className="mt-0.5 flex-shrink-0" />
        <span>
          The OIS is a proprietary composite of twelve institutional dimensions,
          weighted according to the investment objective. Dimensions marked
          &ldquo;Insufficient signal&rdquo; are excluded from the composite and reduce
          overall confidence. Recommendation thresholds: INVEST ≥ 70 · REVIEW 50–69 · DECLINE &lt; 50.
        </span>
      </p>
    </div>
  );
}
